import React, { useMemo } from 'react';
import { Icon } from '@/Components/Icon/Icon';
import { useLocalization } from '@/Hooks';
import { ColumnInfo, ColumnType, OrderAutomatizaionRow } from '@/Types';
import { OrderPackagesCell } from './modules/OrderPackagesCell/OrderPackagesCell';
import { countryTypeUtils } from '@/Utils/CountryTypesUtils';
import { useSelector } from 'react-redux';
import { globalSelectors } from '@/Redux/Global/selectors';
import styles from './OrderAutomatizationTable.scss';

export const useOrderAutomatizationTableColumns = () => {
  const {
    modalWindows: {
      orderLinkWithProduct: { Header },
    },
  } = useLocalization();

  const translation = useSelector(globalSelectors.translation);

  return useMemo((): ColumnInfo<OrderAutomatizaionRow>[] => {
    return [
      {
        headerText: Header.Market,
        fieldName: 'marketplaceImage',
        columnType: ColumnType.String,
        alignment: 'center',
        width: 80,
        render: (rowData) => {
          return (
            <div className={styles.logoBlock}>
              <Icon alt={''} src={rowData.marketplaceImage} className={styles.marketLogo} />
            </div>
          );
        },
      },
      {
        headerText: Header.ID,
        fieldName: 'userOrderId',
        columnType: ColumnType.String,
      },
      {
        headerText: Header.Invoice,
        fieldName: 'invoice',
        columnType: ColumnType.String,
      },
      {
        headerText: Header.Items,
        fieldName: 'orderItems',
        columnType: ColumnType.String,
        children: [
          {
            headerText: Header.Quantity,
            fieldName: 'quantity' as any,
            columnType: ColumnType.String,
            render: ({ orderItems }) => {
              return (
                <div className={styles.cell__items}>
                  {orderItems?.map((item) => (
                    <p key={item.id}>{item.quantity.toIntegerString()}</p>
                  ))}
                </div>
              );
            },
          },
          {
            headerText: Header.ItemName,
            fieldName: 'variants' as any,
            columnType: ColumnType.String,
            width: 200,
            render: ({ orderItems }) => {
              const items = orderItems.map((item) => item.variantTitle);

              return (
                <div className={styles.cell__items} title={items.filter(Boolean).join(' . ')}>
                  {orderItems?.map((item) => (
                    <p key={item.id}>{item.variantTitle}</p>
                  ))}
                </div>
              );
            },
          },
          {
            headerText: Header.UnitPrice,
            fieldName: 'unitPrice' as any,
            columnType: ColumnType.String,
            render: ({ orderItems }) => {
              return (
                <div className={styles.cell__items}>
                  {orderItems?.map((item) => (
                    <p key={item.id}>{item.priceBrutto.toCurrencyString()}</p>
                  ))}
                </div>
              );
            },
          },
          {
            headerText: Header.TotalAmount,
            fieldName: 'totalAmount' as any,
            columnType: ColumnType.String,
            render: ({ orderItems }) => {
              return (
                <div className={styles.cell__items}>
                  {orderItems?.map((item) => (
                    <p key={item.id}>{item.totalPriceBruttoWithoutShipping.toCurrencyString()}</p>
                  ))}
                </div>
              );
            },
          },
        ],
      },
      {
        headerText: Header.Amount,
        fieldName: 'amount',
        columnType: ColumnType.Currency,
      },
      {
        headerText: Header.Country,
        fieldName: 'shippingCountryName',
        columnType: ColumnType.String,
        render: ({ shippingCountryCode }) => (
          <span>{countryTypeUtils.getCountryNameByCountryCode(shippingCountryCode, translation)}</span>
        ),
      },
      {
        headerText: Header.OriginalShippingMethod,
        fieldName: 'originalShippingMethod',
        columnType: ColumnType.String,
        width: 150,
      },
      {
        headerText: Header.NewShippingMethod,
        fieldName: 'selectedPackages',
        columnType: ColumnType.String,
        render: (rowData, rowIndex) => {
          return <OrderPackagesCell rowData={rowData} rowIndex={rowIndex} />;
        },
      },
    ];
  }, [Header]);
};
